
import { Node, Edge } from "@xyflow/react";
import { getContrastColor } from "@/utils/colorUtils";
import { downloadFile } from "./common";

/**
 * Converts nodes to HTML format suitable for Microsoft Word
 */
export const exportToWordHtml = (nodes: Node[], edges: Edge[]): string => {
  let html = `
    <html>
    <head>
      <style>
        .node {
          position: relative;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          margin-bottom: 10px;
          page-break-inside: avoid;
          width: 100%;
          max-width: 600px;
        }
        .node-header {
          padding: 8px;
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          font-weight: bold;
          font-size: 14px;
          text-align: center;
          color: white;
        }
        .node-content {
          padding: 12px;
          background-color: white;
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
          min-height: 80px;
        }
        .client-edit {
          color: #6E59A5;
          font-style: italic;
        }
        .editable {
          border: 1px dashed #ccc;
          padding: 5px;
          min-height: 30px;
        }
        .connections {
          font-size: 12px;
          color: #666;
          margin-top: 10px;
          border-top: 1px solid #eee;
          padding-top: 5px;
        }
      </style>
    </head>
    <body>
      <h1>Whiteboard Export</h1>
      <p>The node positions are locked, but you can edit the text content in the boxes below.</p>
      <div>
  `;

  // Sort nodes to maintain visual hierarchy
  const sortedNodes = [...nodes].sort((a, b) => {
    return a.position.y - b.position.y || a.position.x - b.position.x;
  });

  // Create a map of connections for each node
  const nodeConnections = new Map<string, string[]>();
  edges.forEach(edge => {
    if (!nodeConnections.has(edge.source)) {
      nodeConnections.set(edge.source, []);
    }
    const targetNode = nodes.find(n => n.id === edge.target);
    if (targetNode && targetNode.data.label) {
      // Ensure we're working with a string by explicitly converting
      const label = String(targetNode.data.label || '');
      const labelText = label.length > 30 ? `${label.substring(0, 30)}...` : label;
      nodeConnections.get(edge.source)?.push(
        `${String(targetNode.data.category || 'Node')}: ${labelText}`
      );
    }
  });

  // Add nodes to the HTML
  sortedNodes.forEach(node => {
    const headerColor = node.data.color ? String(node.data.color) : '#9b87f5';
    const textColor = getContrastColor(headerColor);
    const category = String(node.data.category || 'Node');
    const label = String(node.data.label || '');
    const isClientMode = node.data.isClientMode;
    const clientEdits = Array.isArray(node.data.clientEdits) ? node.data.clientEdits : [];
    
    html += `
      <div class="node" id="node-${node.id}" ${isClientMode ? 'style="border: 1px solid #6E59A5;"' : ''}>
        <div class="node-header" style="background-color: ${headerColor}; color: ${textColor};">
          ${category}
        </div>
        <div class="node-content">
          <div class="editable ${clientEdits.length > 0 ? 'client-edit' : ''}" contenteditable="true">
            ${label}
          </div>`;
    
    // Add connections if they exist
    const connections = nodeConnections.get(node.id);
    if (connections && connections.length > 0) {
      html += `<div class="connections">
        <strong>Connected to:</strong>
        <ul>
          ${connections.map(conn => `<li>${conn}</li>`).join('')}
        </ul>
      </div>`;
    }
    
    html += `</div>
      </div>`;
  });

  html += `
      </div>
    </body>
    </html>`;

  return html;
};

/**
 * Handles exporting to Microsoft Word
 */
export const exportToWord = (nodes: Node[], edges: Edge[], title: string = "Whiteboard"): void => {
  try {
    const htmlContent = exportToWordHtml(nodes, edges);
    const fileName = `${String(title).replace(/[^a-z0-9]/gi, '_').toLowerCase()}_export.html`;
    downloadFile(htmlContent, fileName, 'text/html');
  } catch (error) {
    console.error("Failed to export to Word:", error);
    throw error;
  }
};
